import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ThemeConfig {
  id: number;
  name: string;
  app_type: string;
  is_active: boolean;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  background_color: string;
  accent_color: string;
  success_color: string;
  warning_color: string;
  error_color: string;
  info_color: string;
  border_color: string;
  card_color: string;
  header_color: string;
  footer_color: string;
  logo_url?: string;
  font_family: string;
  // Button styling
  button_background_color?: string;
  button_text_color?: string;
  button_hover_color?: string;
  navigation_sidebar_active_color?: string;
  navigation_sidebar_inactive_color?: string;
  navigation_sidebar_text_color?: string;
  navigation_sidebar_hover_color?: string;
  category_button_color?: string;
  // KPI Cards styling
  kpi_card_background?: string;
  kpi_card_text_color?: string;
  kpi_icon_color_1?: string;
  kpi_icon_color_2?: string;
  kpi_icon_color_3?: string;
  kpi_icon_color_4?: string;
  // Status indicators
  status_success_color?: string;
  status_pending_color?: string;
  status_error_color?: string;
  status_info_color?: string;
  // Charts & Data Visualization
  chart_primary_color?: string;
  chart_secondary_color?: string;
  chart_tertiary_color?: string;
  chart_quaternary_color?: string;
}

interface AppTheme {
  theme: ThemeConfig;
  last_updated: string;
  version: number;
}

interface ThemeContextType {
  appTheme: AppTheme;
  loading: boolean;
  error: string | null;
  refreshTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Default theme fallback
const defaultTheme: ThemeConfig = {
  id: 0,
  name: 'Saheeh Dark',
  app_type: 'pos',
  is_active: true,
  primary_color: '#00C896',
  secondary_color: '#2d3048',
  text_color: '#e0e0e0',
  background_color: '#1a1d2e',
  accent_color: '#00C896',
  success_color: '#4CAF50',
  warning_color: '#FF9800',
  error_color: '#F44336',
  info_color: '#2196F3',
  border_color: '#3a3d52',
  card_color: '#252838',
  header_color: '#1e2139',
  footer_color: '#1e2139',
  logo_url: '',
  font_family: 'Poppins',
  button_background_color: '#00C896',
  button_text_color: '#1a1d2e',
  button_hover_color: '#7bc912',
  navigation_sidebar_active_color: '#00C896',
  navigation_sidebar_inactive_color: '#2d3048',
  navigation_sidebar_text_color: '#9ca3af',
  navigation_sidebar_hover_color: 'rgba(255,255,255,0.05)',
  category_button_color: '#00C896',
  kpi_card_background: '#252838',
  kpi_card_text_color: '#e0e0e0',
  kpi_icon_color_1: '#2196F3',
  kpi_icon_color_2: '#4CAF50',
  kpi_icon_color_3: '#FF9800',
  kpi_icon_color_4: '#9C27B0',
  status_success_color: '#4CAF50',
  status_pending_color: '#FF9800',
  status_error_color: '#F44336',
  status_info_color: '#2196F3',
  chart_primary_color: '#00C896',
  chart_secondary_color: '#2196F3',
  chart_tertiary_color: '#FF9800',
  chart_quaternary_color: '#9C27B0',
};

const defaultAppTheme: AppTheme = {
  theme: defaultTheme,
  last_updated: new Date().toISOString(),
  version: 1,
};

const BRAND_VERSION = 'v3-saheeh-default';

export const InstantThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Try to load theme from localStorage first to avoid flash of default theme
  const getInitialTheme = (): AppTheme => {
    try {
      // Version-bust: if brand version changed, clear stale cached theme
      const cachedVersion = localStorage.getItem('pos_theme_version');
      if (cachedVersion !== BRAND_VERSION) {
        localStorage.removeItem('pos_theme_cache');
        localStorage.setItem('pos_theme_version', BRAND_VERSION);
        console.log('🎨 Brand version updated — cleared stale theme cache');
        return defaultAppTheme;
      }
      const cachedTheme = localStorage.getItem('pos_theme_cache');
      if (cachedTheme) {
        const parsedTheme = JSON.parse(cachedTheme);
        console.log('🎨 Loading theme from localStorage on startup');
        return parsedTheme;
      }
    } catch (e) {
      console.warn('⚠️ Failed to load cached theme, using default');
    }
    return defaultAppTheme;
  };
  
  const [appTheme, setAppTheme] = useState<AppTheme>(getInitialTheme());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Cache duration - balance between updates and stability
  const THEME_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes - stable but still updates reasonably fast

  // Separate function to fetch from Firebase
  const fetchThemeFromFirebase = async (isBackground = false, forceRefresh = false) => {
    if (!isBackground) {
      setLoading(true);
      setError(null);
    }
    
    const now = Date.now();
    
    try {
      if (!isBackground) {
        console.log('🎨 [THEME] Loading theme from Firebase...');
      } else {
        console.log('🔄 [THEME] Background fetch checking for theme updates...');
      }
      
      // Use Firebase Theme Service (now that Firestore is configured)
      const FirebaseThemeService = (await import('../services/firebaseThemeService')).default;
      const themeService = FirebaseThemeService.getInstance();
      const appThemeData = await themeService.getThemeConfig();
    
      // Firebase theme service returns the theme directly
      if (appThemeData) {
        // Check if theme was updated by comparing updated_at timestamps
        const cachedThemeStr = localStorage.getItem('pos_theme_cache');
        let themeWasUpdated = false;
        
        if (cachedThemeStr) {
          try {
            const cachedTheme = JSON.parse(cachedThemeStr);
            const cachedUpdatedAt = cachedTheme?.theme?.updated_at;
            const newUpdatedAt = appThemeData.theme.updated_at;
            
            if (cachedUpdatedAt && newUpdatedAt && cachedUpdatedAt !== newUpdatedAt) {
              themeWasUpdated = true;
              console.log('🔄 Theme was updated! Old:', cachedUpdatedAt, 'New:', newUpdatedAt);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
        
        // Only log on first load, forced refresh, or theme update
        if (forceRefresh || lastFetchTime === 0 || themeWasUpdated) {
          console.log('✅ Active theme fetched from Firebase:', appThemeData.theme.name);
          if (themeWasUpdated) {
            console.log('🎨 Applying updated theme colors:', {
              primary: appThemeData.theme.primary_color,
              background: appThemeData.theme.background_color,
              button: appThemeData.theme.button_background_color,
              category: appThemeData.theme.category_button_color,
            });
          }
        }
        
        setAppTheme(appThemeData);
        setLastFetchTime(now); // Update cache time
        
        // Save to localStorage for instant loading
        localStorage.setItem('pos_theme_cache', JSON.stringify(appThemeData));
        localStorage.setItem('pos_theme_cache_time', now.toString());
        localStorage.setItem('pos_theme_cache_expiry', (now + THEME_CACHE_DURATION).toString());
        
        // Apply theme immediately to DOM
        applyThemeToDOM(appThemeData.theme);
        
        // Only log on first load or forced refresh
        if (forceRefresh || lastFetchTime === 0) {
          console.log('🎨 Theme applied successfully from Firebase!');
          console.log('🎨 Applied colors:', {
            primary: appThemeData.theme.primary_color,
            background: appThemeData.theme.background_color,
            text: appThemeData.theme.text_color,
            button: appThemeData.theme.button_background_color,
            navigation: appThemeData.theme.navigation_sidebar_active_color,
            category: appThemeData.theme.category_button_color,
            card: appThemeData.theme.card_color,
          });
        }
      } else {
        console.warn('⚠️ No theme found in Firebase');
        // Try to use cached theme from localStorage if available
        const cachedTheme = localStorage.getItem('pos_theme_cache');
        if (cachedTheme) {
          try {
            const parsedTheme = JSON.parse(cachedTheme);
            console.log('🔄 Using cached theme from localStorage (no Firebase theme found)');
            setAppTheme(parsedTheme);
            applyThemeToDOM(parsedTheme.theme);
            if (!isBackground) {
              setLoading(false);
            }
            return;
          } catch (parseError) {
            console.error('❌ Failed to parse cached theme:', parseError);
          }
        }
        // Only use default if no cache available
        console.warn('⚠️ No theme found and no cache available, using default');
        setAppTheme(defaultAppTheme);
        applyThemeToDOM(defaultTheme);
      }
    } catch (e: any) {
      console.error("❌ Failed to fetch theme configuration:", e);
      
      // ALWAYS try to use cached theme from localStorage first before falling back to default
      const cachedTheme = localStorage.getItem('pos_theme_cache');
      if (cachedTheme) {
        try {
          const parsedTheme = JSON.parse(cachedTheme);
          console.log('🔄 Using cached theme from localStorage due to error');
          setAppTheme(parsedTheme);
          applyThemeToDOM(parsedTheme.theme);
          if (!isBackground) {
            setLoading(false);
          }
          setError(null); // Clear error since we have a cached theme
          return;
        } catch (parseError) {
          console.error('❌ Failed to parse cached theme:', parseError);
        }
      }
      
      // Only use default if no cache available
      // Handle timeout specifically
      if (e.name === 'AbortError') {
        console.warn('⏰ Theme API timeout - using default theme (no cache available)');
        setError("Theme API timeout. Using default theme.");
      } else {
        setError("Failed to load theme. Using default.");
      }
      
      setAppTheme(defaultAppTheme);
      applyThemeToDOM(defaultTheme);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  const fetchTheme = async (forceRefresh = false) => {
    // Check localStorage cache first
    const cachedTheme = localStorage.getItem('pos_theme_cache');
    const cachedTime = localStorage.getItem('pos_theme_cache_time');
    
    if (!forceRefresh && cachedTheme && cachedTime) {
      const cacheAge = Date.now() - parseInt(cachedTime);
      // If cache is still valid (5 minutes), use it BUT still fetch in background
      if (cacheAge < THEME_CACHE_DURATION) {
        try {
          const parsedTheme = JSON.parse(cachedTheme);
          console.log('🎨 [THEME] Using cached theme (age:', Math.round(cacheAge / 1000), 'seconds)');
          setAppTheme(parsedTheme);
          setLastFetchTime(parseInt(cachedTime));
          setLoading(false);
          // Still fetch in background to check for updates (don't await)
          fetchThemeFromFirebase(true, false).catch(err => {
            console.warn('⚠️ [THEME] Background fetch failed:', err);
          });
          return;
        } catch (e) {
          console.warn('⚠️ [THEME] Failed to parse cached theme, fetching fresh');
          // If parsing fails, continue to fetch fresh theme
        }
      } else {
        console.log('🔄 [THEME] Cache expired (age:', Math.round(cacheAge / 1000), 'seconds), fetching fresh');
      }
    }

    // Check if we have cached theme and it's still valid
    const now = Date.now();
    if (!forceRefresh && lastFetchTime > 0 && (now - lastFetchTime) < THEME_CACHE_DURATION) {
      // Silent memory cache usage - no console spam
      setLoading(false);
      return;
    }

    // Fetch from Firebase
    await fetchThemeFromFirebase(false, forceRefresh);
  };

  const applyThemeToDOM = (theme: ThemeConfig) => {
    // Silent theme application - no console spam
    
    // Apply CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primary_color);
    root.style.setProperty('--secondary-color', theme.secondary_color);
    root.style.setProperty('--text-color', theme.text_color);
    root.style.setProperty('--background-color', theme.background_color);
    root.style.setProperty('--accent-color', theme.accent_color);
    root.style.setProperty('--success-color', theme.success_color);
    root.style.setProperty('--warning-color', theme.warning_color);
    root.style.setProperty('--error-color', theme.error_color);
    root.style.setProperty('--info-color', theme.info_color);
    root.style.setProperty('--border-color', theme.border_color);
    // Use background_color for card-color to unify all backgrounds
    root.style.setProperty('--card-color', theme.background_color);
    root.style.setProperty('--header-color', theme.header_color);
    root.style.setProperty('--footer-color', theme.footer_color);
    root.style.setProperty('--font-family', theme.font_family);
    
    // Button colors
    root.style.setProperty('--button-background-color', theme.button_background_color || '#1976D2');
    root.style.setProperty('--button-text-color', theme.button_text_color || '#FFFFFF');
    root.style.setProperty('--button-hover-color', theme.button_hover_color || '#1565C0');
    
    // Navigation colors
    root.style.setProperty('--nav-active-color', theme.navigation_sidebar_active_color || '#00C896');
    root.style.setProperty('--nav-inactive-color', theme.navigation_sidebar_inactive_color || '#f8f9fa');
    root.style.setProperty('--nav-text-color', theme.navigation_sidebar_text_color || '#495057');
    root.style.setProperty('--nav-hover-color', theme.navigation_sidebar_hover_color || '#e9ecef');
    
    // Category button colors
    root.style.setProperty('--category-button-color', theme.category_button_color || theme.primary_color || '#FF9800');
    root.style.setProperty('--category-button-text-color', theme.text_color || '#000000');
    
    // KPI Card colors
    root.style.setProperty('--kpi-card-background', theme.kpi_card_background || '#ffffff');
    root.style.setProperty('--kpi-card-text', theme.kpi_card_text_color || '#000000');
    root.style.setProperty('--kpi-icon-1', theme.kpi_icon_color_1 || '#1976D2');
    root.style.setProperty('--kpi-icon-2', theme.kpi_icon_color_2 || '#4CAF50');
    root.style.setProperty('--kpi-icon-3', theme.kpi_icon_color_3 || '#FF9800');
    root.style.setProperty('--kpi-icon-4', theme.kpi_icon_color_4 || '#9C27B0');
    
    // Status colors
    root.style.setProperty('--status-success', theme.status_success_color || '#4CAF50');
    root.style.setProperty('--status-pending', theme.status_pending_color || '#FF9800');
    root.style.setProperty('--status-error', theme.status_error_color || '#F44336');
    root.style.setProperty('--status-info', theme.status_info_color || '#2196F3');
    
    // Chart colors
    root.style.setProperty('--chart-primary', theme.chart_primary_color || '#4CAF50');
    root.style.setProperty('--chart-secondary', theme.chart_secondary_color || '#FF6B6B');
    root.style.setProperty('--chart-tertiary', theme.chart_tertiary_color || '#1976D2');
    root.style.setProperty('--chart-quaternary', theme.chart_quaternary_color || '#FF9800');
    
    // Apply to document body
    document.body.style.backgroundColor = theme.background_color;
    document.body.style.color = theme.text_color;
    document.body.style.fontFamily = theme.font_family;
    
    // Update favicon if logo_url is provided
    if (theme.logo_url) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = theme.logo_url;
      }
    }
    
    // Theme applied silently - no console spam
  };

  const refreshTheme = async () => {
    // Disabled - was causing too many problems
    console.log('⚠️ Refresh theme function disabled');
  };

  useEffect(() => {
    // Only fetch theme once on startup - no more background fetching
    fetchTheme();
    
    // Add a fallback timeout to prevent infinite loading
    const fallbackTimeout = setTimeout(() => {
      if (loading) {
        console.warn('⏰ Theme loading timeout - checking for cached theme');
        // Try to use cached theme from localStorage first
        const cachedTheme = localStorage.getItem('pos_theme_cache');
        if (cachedTheme) {
          try {
            const parsedTheme = JSON.parse(cachedTheme);
            console.log('🔄 Using cached theme from localStorage (timeout)');
            setAppTheme(parsedTheme);
            applyThemeToDOM(parsedTheme.theme);
            setLoading(false);
            setError(null);
            return;
          } catch (parseError) {
            console.error('❌ Failed to parse cached theme:', parseError);
          }
        }
        // Only use default if no cache available
        console.warn('⏰ No cached theme available, using default');
        setAppTheme(defaultAppTheme);
        setLoading(false);
        setError('Theme loading timeout - using default');
      }
    }, 15000); // 15 second fallback (increased from 8)
    
    return () => {
      clearTimeout(fallbackTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value: ThemeContextType = {
    appTheme,
    loading,
    error,
    refreshTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useInstantTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useInstantTheme must be used within an InstantThemeProvider');
  }
  return context;
};
